<template>
  <b-modal
    v-model="visible"
    :busy="busy"
    scrollable
    title="Đổi phụ trách chăm sóc bệnh nhân"
    no-close-on-esc
    no-close-on-backdrop
    no-enforce-focus
    size="xxl"
    ok-title="Gửi"
    cancel-title="Hủy"
    @ok="submit"
    @hidden="hide"
  >
    <div class="row">
      <div class="col-md">
        <user-select
          label="Chọn bác sĩ"
          :model="formModel"
          attribute="doctorUser"
          :errors="formErrors"
          :roles-filter="['bac-sy', 'bac-sy-pho-nhom']"
          :group-filter="groupFilter"
          v-if="['owner', 'bac-sy-truong-nhom', 'bac-sy-pho-nhom'].includes($store.state.user.role)"
          @change="formModel.followUser = null"
        ></user-select>
        <user-select
          label="Chọn tình nguyện viên"
          :model="formModel"
          attribute="followUser"
          :errors="formErrors"
          :role-filter="'nvyt'"
          :group-filter="groupFilter"
          :doctor-group-filter="formModel.doctorUser || null"
        ></user-select>
        <user-select
          label="Chọn BSTN/BSPN (bác sỹ trưởng nhóm hoặc bác sỹ phó nhóm sẽ trực tiếp theo dõi khi bệnh nhân ở NC3 hoặc NC4)"
          :model="formModel"
          attribute="seniorDoctorUser"
          :errors="formErrors"
          :roles-filter="['bac-sy-truong-nhom', 'bac-sy-pho-nhom']"
          :group-filter="groupFilter"
          v-if="['owner', 'bac-sy-truong-nhom', 'bac-sy-pho-nhom'].includes($store.state.user.role)"
        ></user-select>
      </div>
    </div>
    <div class="row">
      <div v-if="listBenhNhan.length > 0" class="col-md c-template-table table-responsive">
        <h5>Cập nhật người chăm sóc cho {{ listBenhNhan.length }} bệnh nhân dưới đây</h5>
        <table class="table table-bordered table-more table-hover">
          <thead>
            <tr>
              <th class="th-number">#</th>
              <th>Bệnh nhân</th>
              <th class="text-center">Nhóm NC</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in listBenhNhan" :key="item._id">
              <td class="th-number">{{ index + 1 }}</td>
              <td>
                <p>
                  Tên BN: <b>{{ item.name ? item.name : $t('unknown') }}</b>
                </p>
                <p>
                  Năm sinh: <b>{{ item.dob ? item.dob : $t('unknown') }}</b>
                </p>
                <p>
                  Giới tính: <b>{{ item.gender === 'male' ? 'Nam' : item.gender === 'female' ? 'Nữ' : 'Không rõ' }}</b>
                </p>
                <p v-if="item.stopFollowTime"><span class="badge badge-secondary">Đã ngừng theo dõi</span></p>
              </td>
              <td class="text-center">
                <p>
                  <span v-if="!item.tier" class="badge badge-secondary">Chưa cập nhật</span>
                  <span v-else-if="item.tier === 'nc0'" class="badge badge-success">NC0</span>
                  <span v-else-if="item.tier === 'nc1'" class="badge badge-info">NC1</span>
                  <span v-else-if="item.tier === 'nc2'" class="badge badge-primary">NC2</span>
                  <span v-else-if="item.tier === 'nc3'" class="badge badge-warning">NC3</span>
                  <span v-else class="badge badge-danger">NC4</span>
                </p>
                <br />
                <p v-if="item.nextTier && item.tier && item.nextTier !== item.tier">
                  <span class="badge badge-warning glow"
                    ><b-spinner type="grow" small></b-spinner> Cần duyệt chuyển {{ item.nextTier }}</span
                  >
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <template #modal-ok>
      <b-spinner v-if="busy" small variant="primary"></b-spinner>
      {{ $t('save') }}
    </template>
    <template #modal-cancel>
      {{ $t('cancel') }}
    </template>
  </b-modal>
</template>

<script>
import UserSelect from '@/components/UserSelect';

const DEFAULT_FORM_MODEL = {
  ids: [], // list of benh nhan ids
  seniorDoctorUser: null,
  doctorUser: null, // doctor user id or null
  followUser: null, // follow user id or null
};

export default {
  components: {
    UserSelect,
  },
  inheritAttrs: false,
  data() {
    return {
      busy: false,
      visible: false,
      listBenhNhan: [],
      formModel: DEFAULT_FORM_MODEL,
      formErrors: [],
    };
  },
  computed: {
    groupFilter() {
      return this.listBenhNhan.length === 1
        ? this.listBenhNhan[0].group._id
        : this.$store.state.user.role !== 'owner'
        ? this.$store.state.user.group
        : null;
    },
  },
  methods: {
    show({listBenhNhan}) {
      const {doctorUser = null, followUser = null, seniorDoctorUser = null} =
        listBenhNhan.length === 1 ? listBenhNhan[0] : {};
      this.listBenhNhan = listBenhNhan;
      this.formModel = {
        ids: listBenhNhan.map(benhNhan => benhNhan._id),
        seniorDoctorUser: seniorDoctorUser ? seniorDoctorUser._id : null,
        doctorUser: doctorUser ? doctorUser._id : null,
        followUser: followUser ? followUser._id : null,
      };
      this.formErrors = [];
      this.visible = true;
    },
    hide() {
      this.listBenhNhan = [];
      this.formModel = DEFAULT_FORM_MODEL;
      this.formErrors = [];
      this.visible = false;
    },
    async submit(event) {
      event.preventDefault();
      try {
        this.busy = true;
        const response = await this.$service({
          url: '/benh-nhan/change-user-in-charge',
          method: 'post',
          data: this.formModel,
        });
        if (!response) throw Error('Thất bại');
        this.$swal({
          title: this.$t('success'),
          text: 'Cập nhật phụ trách bệnh nhân thành công',
          icon: 'success',
          toast: true,
          position: 'top-end',
          timer: 5000,
        });
      } catch (error) {
        this.$swal({
          title: this.$t('error'),
          text: 'Cập nhật phụ trách bệnh nhân thất bại',
          icon: 'error',
          toast: true,
          position: 'top-end',
          timer: 5000,
        });
      } finally {
        this.busy = false;
        this.visible = false;
        this.$bus.$emit('reloadBenhNhanList');
      }
    },
  },
};
</script>
